/* global Word */
import * as React from "react";
import { redirectToAuth, isLoggedIn } from "../shared/authService";
import Header, { StatusTone } from "./components/Header";
import ReviewSummary from "./components/ReviewSummary";
import MessageBubble from "./components/MessageBubble";
import SuggestionCard from "./components/SuggestionCard";
import TypingIndicator from "./components/TypingIndicator";
import ThinkingMessage from "./components/ThinkingMessage";
import ProgressBubble from "./components/ProgressBubble";
import JurisdictionBubble from "./components/JurisdictionBubble";
import { checkIcon, declineIcon, sendIcon } from "./icons";
import { Suggestion, TimelineItem } from "./types";
import { callStreamingReview, ReviewJurisdiction } from "./services/reviewStreamingService";
import "./styles/base.css";
import "./styles/chat.css";
import "./styles/input.css";
import "./styles/toast.css";

const formatTimestamp = () => new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(new Date());
const generateId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const quickActions = [
  { label: "Review document", prompt: "Review the entire document for legal issues" },
  { label: "Find issues", prompt: "Check for ambiguous or unclear clauses" },
  { label: "Simplify language", prompt: "Simplify legal jargon to plain language" },
];

const Toast: React.FC<{ message: string; tone: "success" | "error" }> = ({ message, tone }) => (
  <div className={`toast show ${tone === "success" ? "success" : "error"}`}>
    {tone === "success" ? checkIcon : declineIcon}
    <span id="toastMessage">{message}</span>
  </div>
);

const ErrorCard: React.FC<{ text: string }> = ({ text }) => (
  <div className="error-card">
    <div className="error-card-title">
      {declineIcon}
      Unable to complete review
    </div>
    <div className="error-card-text">{text}</div>
  </div>
);

type StatusState = { text: string; tone: StatusTone };

const useStatusManager = (initial: StatusState) => {
  const [status, setStatus] = React.useState<StatusState>(initial);

  const setReady = React.useCallback((text = "Ready") => setStatus({ text, tone: "ready" }), []);
  const setLoading = React.useCallback((text = "Loading...") => setStatus({ text, tone: "loading" }), []);
  const setAnalyzing = React.useCallback((text = "Analyzing...") => setStatus({ text, tone: "analyzing" }), []);
  const setError = React.useCallback((text = "Error") => setStatus({ text, tone: "error" }), []);

  return { status, setReady, setLoading, setAnalyzing, setError };
};

const LegalReviewerApp: React.FC = () => {
  const [timeline, setTimeline] = React.useState<TimelineItem[]>([]);
  const [input, setInput] = React.useState("");
  const [isTyping, setIsTyping] = React.useState(false);
  const [toast, setToast] = React.useState<{ message: string; tone: "success" | "error" } | null>(null);
  const { status, setReady, setLoading, setAnalyzing, setError } = useStatusManager({ text: "Ready", tone: "ready" });
  const [showWelcome, setShowWelcome] = React.useState(true);
  const [thinkingMessages, setThinkingMessages] = React.useState<string[]>([]);
  const [progressUpdates, setProgressUpdates] = React.useState<Array<{ id: string; section: string; type?: string }>>([]);
  const [jurisdiction, setJurisdiction] = React.useState<{ data: ReviewJurisdiction; timestamp: string } | null>(null);

  const lastThinkingRef = React.useRef<string>("");
  const errorHandledRef = React.useRef(false);
  const chatRef = React.useRef<HTMLDivElement>(null);
  //
  // React.useEffect(() => {
  //   if (!document.getElementById("reviewer-style-block")) {
  //     const style = document.createElement("style");
  //     style.id = "reviewer-style-block";
  //     style.textContent = reviewerCss as string;
  //     document.head.appendChild(style);
  //   }
  // }, []);

  const suggestions = React.useMemo(
    () => timeline.filter((item): item is Extract<TimelineItem, { kind: "suggestion" }> => item.kind === "suggestion").map((item) => item.suggestion),
    [timeline]
  );

  const counts = React.useMemo(() => {
    const total = suggestions.length;
    const pending = suggestions.filter((s) => s.status === "pending").length;
    const accepted = suggestions.filter((s) => s.status === "accepted").length;
    const declined = suggestions.filter((s) => s.status === "declined").length;
    return { total, pending, accepted, declined };
  }, [suggestions]);

  React.useEffect(() => {
    const el = chatRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [timeline, thinkingMessages, progressUpdates, jurisdiction, isTyping]);

  React.useEffect(() => {
    if (!isLoggedIn()) {
      redirectToAuth();
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key === "xob_auth_status" && event.newValue !== "loggedIn") {
        redirectToAuth();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const showToast = React.useCallback((message: string, tone: "success" | "error") => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 2200);
  }, []);

  const getDocumentText = React.useCallback(async () => {
    if (typeof Word === "undefined") {
      throw new Error("Open this add-in inside Office Word to analyze the document.");
    }
    try {
      const text: string = await Word.run(async (context) => {

        const ccs = context.document.contentControls;
        ccs.load("items/tag,items/title,items/text");
        await context.sync();

        console.log(ccs.items);
        for (let item of ccs.items) {
          console.log(item);
        }

        const body = context.document.body;
        body.load("text");
        await context.sync();

        return body.text;
      });
      if (!text || text.trim().length < 5) {
        throw new Error("Document is empty or too short.");
      }
      return text;
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Unable to read document.");
      throw err;
    }
  }, []);

  const addTimelineItem = React.useCallback((item: TimelineItem) => {
    setTimeline((prev) => [...prev, item]);
  }, []);

  const updateSuggestion = React.useCallback((id: string, updater: (s: Suggestion) => Suggestion) => {
    setTimeline((prev) =>
      prev.map((item) => {
        if (item.kind === "suggestion" && item.suggestion.id === id) {
          return { ...item, suggestion: updater(item.suggestion) };
        }
        return item;
      })
    );
  }, []);

  const handleSend = React.useCallback(
    async (preset?: string) => {
      const text = (preset ?? input).trim();
      if (!text) {
        return;
      }

      setShowWelcome(false);
      setThinkingMessages([]);
      setProgressUpdates([]);
      setJurisdiction(null);
      errorHandledRef.current = false;

      const timestamp = formatTimestamp();
      addTimelineItem({ id: generateId(), kind: "text", role: "user", text, timestamp });
      setInput("");
      setIsTyping(true);
      setLoading("Reading document...");

      try {
        const documentText = await getDocumentText();
        await callStreamingReview({
          prompt: text,
          documentText,
          callbacks: {
            onStatus: (state, message) => {
              if (state === "loading") {
                const statusText = message || "Loading...";
                if (statusText.toLowerCase().includes("analyzing")) {
                  setAnalyzing(statusText);
                } else {
                  setLoading(statusText);
                }
              } else if (state === "ready") {
                setReady(message || "Ready");
              } else if (state === "error") {
                setError(message || "Error");
              }
            },
            onThinking: (thinkingText) => {
              const normalized = thinkingText?.trim() || "";
              if (!normalized || normalized.length < 3) return;
              if (lastThinkingRef.current === normalized) return;
              lastThinkingRef.current = normalized;
              setThinkingMessages((prev) => [...prev, normalized]);
            },
            onProgress: (section, type) => {
              if (!section) return;
              setProgressUpdates((prev) => {
                if (prev.some((item) => item.section === section)) {
                  return prev;
                }
                return [...prev, { id: generateId(), section, type }];
              });
            },
            onComplete: (result) => {
              setThinkingMessages([]);
              lastThinkingRef.current = "";
              setProgressUpdates([]);

              if (result.jurisdiction) {
                setJurisdiction({ data: result.jurisdiction, timestamp: formatTimestamp() });
              }

              const suggestionsFromApi: Suggestion[] = (result.suggestions || []).map((sugg) => ({
                id: (sugg as any).id || generateId(),
                section: sugg.section || "General",
                original: sugg.original || "",
                revised: sugg.revised || "",
                reason: sugg.reason || "Proposed change",
                status: "pending",
              }));

              if (suggestionsFromApi.length === 0) {
              addTimelineItem({
                id: generateId(),
                kind: "text",
                role: "assistant",
                text: result.summary || "I didn't find any specific issues to flag. Ask for another angle or provide more detail and I'll take another pass.",
                timestamp: formatTimestamp(),
              });
              return;
            }

            addTimelineItem({
              id: generateId(),
              kind: "text",
              role: "assistant",
              text: result.summary || "Here are the most impactful revisions for your document.",
              timestamp: formatTimestamp(),
            });

              suggestionsFromApi.forEach((sugg) => {
                addTimelineItem({
                  id: sugg.id,
                  kind: "suggestion",
                  suggestion: sugg,
                  timestamp: formatTimestamp(),
                });
              });
            },
            onError: (error) => {
              errorHandledRef.current = true;
              setError("Error");
              addTimelineItem({
                id: generateId(),
                kind: "error",
                text: error?.message || "Unable to analyze document. Please try again.",
                timestamp: formatTimestamp(),
              });
              setToast(null);
            },
          },
        });
      } catch (error: any) {
        if (!errorHandledRef.current) {
          setError("Error");
          addTimelineItem({
            id: generateId(),
            kind: "error",
            text: error?.message || "Unable to analyze document. Please try again.",
            timestamp: formatTimestamp(),
          });
          setToast(null);
        }
      } finally {
        setIsTyping(false);
        setThinkingMessages([]);
        lastThinkingRef.current = "";
        setProgressUpdates([]);
      }
    },
    [addTimelineItem, getDocumentText, input, setAnalyzing, setError, setLoading, setReady]
  );

  const goToSuggestion = React.useCallback(
    async (id: string) => {
      const target = suggestions.find((s) => s.id === id);
      if (!target) return;

      if (typeof Word === "undefined") {
        showToast("Word is not available. Open this add-in inside Word.", "error");
        return;
      }

      try {
        await Word.run(async (context) => {
          const body = context.document.body;
          const primarySearch = body.search(target.original, {
            matchCase: false,
            matchWholeWord: false,
          });
          const secondarySearch =
            target.revised && target.revised !== target.original
              ? body.search(target.revised, { matchCase: false, matchWholeWord: false })
              : null;

          primarySearch.load("items");
          if (secondarySearch) {
            secondarySearch.load("items");
          }
          await context.sync();

          let range = primarySearch.items[0];
          if ((!range || primarySearch.items.length === 0) && secondarySearch && secondarySearch.items.length > 0) {
            range = secondarySearch.items[0];
          }

          if (!range) {
            throw new Error("Suggestion text not found.");
          }

          range.select("Select");
          await context.sync();
        });
        showToast("Jumped to suggestion", "success");
      } catch (error) {
        console.log("Error navigating to suggestion", error);
        showToast("Could not find this text in the document", "error");
      }
    },
    [suggestions, showToast]
  );

  const applySuggestion = React.useCallback(
    async (id: string) => {
      const target = suggestions.find((s) => s.id === id);
      if (!target) return;

      try {
        if (typeof Word === "undefined") {
          showToast("Word is not available. Open this add-in inside Word.", "error");
          return;
        }

        await Word.run(async (context) => {
          const results = context.document.body.search(target.original, {
            matchCase: false,
            matchWholeWord: false,
          });
          results.load("items");
          await context.sync();

          if (results.items.length > 0) {
            results.items[0].insertText(target.revised, Word.InsertLocation.replace);
            await context.sync();
          }
        });
        updateSuggestion(id, (s) => ({ ...s, status: "accepted" }));
        showToast("Change applied to document", "success");
      } catch (error) {
        console.log("Error applying change", error);
        showToast("Could not apply change", "error");
      }
    },
    [suggestions, updateSuggestion, showToast]
  );

  const declineSuggestion = React.useCallback(
    (id: string) => {
      updateSuggestion(id, (s) => ({ ...s, status: "declined" }));
      showToast("Suggestion declined", "success");
    },
    [updateSuggestion, showToast]
  );

  const undoSuggestion = React.useCallback(
    (id: string) => {
      updateSuggestion(id, (s) => ({ ...s, status: "pending" }));
    },
    [updateSuggestion]
  );

  const regenerateSuggestion = React.useCallback(
    (id: string) => {
      setIsTyping(true);
      setTimeout(() => {
        updateSuggestion(id, (s) => ({
          ...s,
          status: "pending",
          revised: `${s.revised} (alternative draft)`,
        }));
        setIsTyping(false);
      }, 800);
    },
    [updateSuggestion]
  );

  const acceptAll = React.useCallback(() => {
    suggestions.filter((s) => s.status === "pending").forEach((s) => applySuggestion(s.id));
  }, [suggestions, applySuggestion]);

  const declineAll = React.useCallback(() => {
    suggestions.filter((s) => s.status === "pending").forEach((s) => declineSuggestion(s.id));
  }, [suggestions, declineSuggestion]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Header status={status} />

      <ReviewSummary total={counts.total} pending={counts.pending} accepted={counts.accepted} declined={counts.declined} onAcceptAll={acceptAll} onDeclineAll={declineAll} />

      <div className="chat-container" ref={chatRef}>
        {showWelcome && (
          <div className="welcome-card" id="welcomeCard">
            <div className="welcome-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h2 className="welcome-title">How can I help you today?</h2>
            <p className="welcome-text">I can review your legal document, suggest edits, and help ensure clarity and compliance.</p>
            <div className="quick-actions">
              {quickActions.map((action) => (
                <button key={action.label} className="quick-action" onClick={() => handleSend(action.prompt)}>
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {timeline.map((item) => {
          if (item.kind === "text") {
            return <MessageBubble key={item.id} role={item.role} text={item.text} timestamp={item.timestamp} />;
          }
          if (item.kind === "error") {
            return <ErrorCard key={item.id} text={item.text} />;
          }
          return (
            <SuggestionCard
              key={item.id}
              suggestion={item.suggestion}
              timestamp={item.timestamp}
              onApply={applySuggestion}
              onDecline={declineSuggestion}
              onUndo={undoSuggestion}
              onRegenerate={regenerateSuggestion}
              onGoTo={goToSuggestion}
            />
          );
        })}
        {jurisdiction && <JurisdictionBubble jurisdiction={jurisdiction.data} timestamp={jurisdiction.timestamp} />}
        {progressUpdates.map((item) => (
          <ProgressBubble key={item.id} section={item.section} type={item.type} />
        ))}
        {thinkingMessages.map((msg, index) => (
          <ThinkingMessage key={`${index}-${msg}`} text={msg} />
        ))}
        {isTyping && <TypingIndicator />}
      </div>

      <div className="input-area">
        <div className="input-wrapper">
          <div className="input-container">
            <textarea
              className="input-field"
              id="messageInput"
              placeholder="Ask me to review or edit your document..."
              rows={1}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              disabled={status.tone === "loading" || status.tone === "analyzing"}
            />
            <button
              className="send-btn"
              id="sendBtn"
              onClick={() => handleSend()}
              disabled={!input.trim() || status.tone === "loading" || status.tone === "analyzing"}
            >
              {sendIcon}
            </button>
          </div>
        </div>
        <div className="input-hint">
          <kbd>Enter</kbd> to send · <kbd>Shift + Enter</kbd> for new line
        </div>
      </div>

      {toast && <Toast message={toast.message} tone={toast.tone} />}
    </div>
  );
};

export default LegalReviewerApp;
